import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/providers/auth-provider'
import { useAuthStore } from '@/stores/auth-store'
import { AtSign, Eye, EyeOff, Loader2 } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Simple validation replacement for now
const validateLogin = (email: string, password: string) => {
  const errors: { email?: string; password?: string } = {}
  if (!email || !email.includes('@')) errors.email = 'Invalid email address'
  if (!password || password.length < 6) errors.password = 'Password must be at least 6 characters'
  return { isValid: Object.keys(errors).length === 0, errors }
}

interface Particle {
  id: number
  x: number
  y: number
  size: number
  color: string
  opacity: number
  xOffset: number
  yOffset: number
  transitionSpeed: number
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { autoLogin } = useAuth()
  const userLogin = useAuthStore((state) => state.userLogin)

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [particles, setParticles] = useState<Particle[]>([])
  const particleContainer = useRef<HTMLDivElement>(null)

  // Form state
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})

  const isDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.includes('dev') ||
    window.location.port === '8080' ||
    window.location.port === '3000' ||
    window.location.port === '5173'

  // Create interactive background particles
  const createParticles = useCallback(() => {
    const primaryColor = '#6F59FD'
    const secondaryColor = '#ff6f60'
    const particleCount = 15

    const newParticles: Particle[] = []

    for (let i = 0; i < particleCount; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 120 + 40,
        color: Math.random() > 0.7 ? primaryColor + '15' : secondaryColor + '10',
        opacity: Math.random() * 0.3 + 0.05,
        xOffset: 0,
        yOffset: 0,
        transitionSpeed: Math.random() * 150 + 80
      })
    }

    setParticles(newParticles)
  }, [])

  // Handle mouse movement for interactive effect
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!particleContainer.current) return

    const rect = particleContainer.current.getBoundingClientRect()
    const mouseX = event.clientX - rect.left
    const mouseY = event.clientY - rect.top

    const centerX = rect.width / 2
    const centerY = rect.height / 2

    // Calculate offset from center (normalized)
    const offsetX = (mouseX - centerX) / centerX
    const offsetY = (mouseY - centerY) / centerY

    // Update particles position based on mouse movement
    setParticles(prev => prev.map(particle => {
      // Calculate movement factor based on position (3D-like effect)
      const moveFactor = 40 * (particle.size / 150)

      return {
        ...particle,
        xOffset: -offsetX * moveFactor,
        yOffset: -offsetY * moveFactor
      }
    }))
  }, [])

  // Mock data for dev bypass
  const loadMockData = () => {
    const mockData = {
      devices: [
        {
          id: '1',
          deviceId: 'DEMO123',
          name: 'Demo Restaurant Device',
          type: 'RESTAURANT',
          deviceConfiguration: {
            restaurantName: 'Demo Restaurant',
            welcomeMessage: 'Willkommen im Demo Restaurant!'
          }
        }
      ],
      customerAnswers: {
        categories: [
          {
            id: '1',
            name: 'Begrüßung',
            type: 'ANSWER' as const,
            description: 'Begrüßungsnachrichten'
          }
        ]
      }
    }

    // Store in localStorage
    localStorage.setItem('mockDevices', JSON.stringify(mockData.devices))
    localStorage.setItem('mockCustomerAnswers', JSON.stringify(mockData.customerAnswers))
    localStorage.setItem('currentDeviceSerialId', 'DEMO123')
  }

  // Dev login bypass function
  const devLoginBypass = async () => {
    try {
      setIsLoading(true)
      toast({
        title: "Dev Bypass",
        description: "Dev Bypass Login wird durchgeführt..."
      })

      // Generate fake tokens with expiry
      const fakeToken = 'dev-bypass-jwt-token-' + Date.now()
      const fakeRefreshToken = 'dev-bypass-refresh-token-' + Date.now()
      const expiryTime = Date.now() + (24 * 60 * 60 * 1000) // 24 hours

      // Store fake tokens
      localStorage.setItem('token', fakeToken)
      localStorage.setItem('refreshToken', fakeRefreshToken)
      localStorage.setItem('tokenExpires', expiryTime.toString())
      localStorage.setItem('user_email', 'dev@example.com')
      localStorage.setItem('dev_bypass_mode', 'true')

      // Load mock data
      loadMockData()

      // Wait a bit for stores to initialize
      await new Promise(resolve => setTimeout(resolve, 500))

      toast({
        title: "Erfolg",
        description: "Dev Login erfolgreich! Stores initialisiert."
      })

      // Navigate to main app
      setTimeout(() => {
        navigate('/customer/activity')
      }, 1000)

    } catch (error) {
      console.error('Dev login bypass error:', error)
      toast({
        title: "Fehler",
        description: 'Dev Login Fehler: ' + (error as Error).message,
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Regular login function
  const onSubmit = useCallback(async (data: { email: string; password: string }) => {
    setIsLoading(true)

    // Save email to localStorage for next time
    localStorage.setItem('user_email', data.email)

    try {
      await userLogin({
        email: data.email,
        password: data.password
      })

      // Show success notification
      toast({
        title: "Erfolg",
        description: "Anmeldung erfolgreich!"
      })

      // Trigger AuthProvider to load user from token
      console.log('🔄 LOGIN: Triggering AuthProvider autoLogin...')
      await autoLogin()
      console.log('✅ LOGIN: AuthProvider autoLogin completed')

      // Initialize app stores BEFORE navigation
      try {
        const { useConfigurationStore } = await import('@/stores/configuration-store')
        const { useCustomerStore } = await import('@/stores/customer-store')
        const { useDevicesStore } = await import('@/stores/devices-store')
        const { useAuthStore } = await import('@/stores/auth-store')
        
        const configStore = useConfigurationStore.getState()
        const customerStore = useCustomerStore.getState()
        const devicesStore = useDevicesStore.getState()
        const authStore = useAuthStore.getState()
        
        // Get auth headers for configuration
        const headers = authStore.getApiHeaders()
        
        console.log('🔄 LOGIN: Initializing stores after login...')
        
        // Initialize configuration (languages, voices)
        await configStore.initBaseConfiguration(headers)
        console.log('✅ LOGIN: Configuration initialized')
        
        // Initialize devices (critical for data loading!)
        await devicesStore.initializeDevices()
        console.log('✅ LOGIN: Devices initialized')
        
        // Reset customer store
        await customerStore.reset()
        console.log('✅ LOGIN: Customer store reset')
        
      } catch (error) {
        console.error('❌ LOGIN: Error initializing app stores:', error)
        toast({
          title: "Warnung",
          description: "Einige Daten konnten nicht geladen werden.",
          variant: "destructive"
        })
      }

      // Small delay to ensure stores are ready
      await new Promise(resolve => setTimeout(resolve, 500))
      console.log('🚀 LOGIN: Navigating to activity page...')

      // Navigate to main app after initialization
      navigate('/customer/activity', { replace: true })

    } catch (error) {
      // Clear password field on error for security
      setFormData(prev => ({ ...prev, password: '' }))

      // Detailed error handling based on different error types
      let errorMessage = "Login fehlgeschlagen. Bitte versuchen Sie es erneut."

      if (error instanceof Error) {
        if (error.message === 'UNKNOWN_ERROR') {
          errorMessage = "Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung."
        } else {
          errorMessage = error.message
        }
      }

      toast({
        title: "Anmeldung fehlgeschlagen",
        description: errorMessage,
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }, [userLogin, navigate, toast])

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev)
  }, [])

  const handleFormSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate form
    const validation = validateLogin(formData.email, formData.password)
    setErrors(validation.errors)

    if (!validation.isValid) {
      return
    }

    await onSubmit(formData)
  }, [formData, onSubmit])

  useEffect(() => {
    // Load saved email from localStorage
    const savedEmail = localStorage.getItem('user_email')
    if (savedEmail) {
      setFormData({
        email: isDevelopment ? 'test@dev.local' : savedEmail,
        password: isDevelopment ? 'test123' : ''
      })
    } else {
      setFormData({
        email: isDevelopment ? 'test@dev.local' : '',
        password: isDevelopment ? 'test123' : ''
      })
    }

    // Auto-focus email field
    setTimeout(() => {
      const emailField = document.getElementById('email')
      if (emailField) {
        emailField.focus()
      }
    }, 100)

    // Initialize particles
    createParticles()

    // Keyboard event listeners
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.target as HTMLElement).id === 'email') {
        setTimeout(() => {
          const passwordField = document.getElementById('password')
          if (passwordField) {
            passwordField.focus()
          }
        }, 100)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [createParticles])

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 to-gray-900">
      <div className="min-h-screen flex flex-col items-center justify-center relative p-4 sm:p-6 lg:p-8 w-full">
        {/* Interactive background elements */}
        <div
          ref={particleContainer}
          className="fixed inset-0 overflow-hidden z-0"
          onMouseMove={handleMouseMove}
        >
          {/* Animated particles */}
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full blur-md transition-transform ease-out"
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                backgroundColor: particle.color,
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                transform: `translateX(${particle.xOffset}px) translateY(${particle.yOffset}px)`,
                transitionDuration: `${particle.transitionSpeed}ms`
              }}
            />
          ))}

          {/* Decorative grid pattern */}
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        </div>

        {/* Content container */}
        <div className="z-10 w-full max-w-sm sm:max-w-md lg:max-w-lg flex flex-col items-center relative">
          {/* Logo */}
          <div className="mb-6 sm:mb-8 lg:mb-10 flex items-center">
            <a href="/" className="relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full blur-md opacity-75 group-hover:opacity-100 transition duration-1000"></div>
              <div className="relative">
                <img
                  className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 z-10"
                  src="/src/assets/assistentAiSignet.svg"
                  alt="Assistent.ai Logo"
                />
              </div>
            </a>
          </div>

          {/* Auth Card */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-2xl px-6 sm:px-8 lg:px-10 py-8 sm:py-10 lg:py-12 w-full border border-white/20 transition-all animation-fade">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-medium text-center mb-6 sm:mb-8 lg:mb-10 text-white">
              Jetzt anmelden
            </h1>

            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-1">
                <div className="relative group">
                  <Label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                    E-Mail
                  </Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="E-Mail"
                      autoComplete="email"
                      disabled={isLoading}
                      className={`
                        block w-full py-3.5 pl-4 pr-12 text-gray-100 bg-white/5 border border-gray-500/30 rounded-xl shadow-inner 
                        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors 
                        focus-visible:ring-2 focus-visible:ring-brand-primary
                        ${errors.email ? 'border-red-500/50 focus:ring-red-500 focus:border-red-500' : ''}
                      `}
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <AtSign className={`h-5 w-5 ${errors.email ? 'text-red-400' : 'text-gray-400'}`} />
                    </div>

                    {/* Visual highlight effect on focus */}
                    <div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-focus-within:opacity-20 bg-gradient-to-r from-brand-primary to-brand-secondary transition-opacity"></div>
                  </div>
                  {errors.email && (
                    <p className="text-sm text-red-400 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <div className="relative group">
                  <Label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                    Passwort
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Passwort"
                      autoComplete="current-password"
                      disabled={isLoading}
                      className={`
                        block w-full py-3.5 pl-4 pr-12 text-gray-100 bg-white/5 border border-gray-500/30 rounded-xl shadow-inner 
                        focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-colors 
                        focus-visible:ring-2 focus-visible:ring-brand-primary
                        ${errors.password ? 'border-red-500/50 focus:ring-red-500 focus:border-red-500' : ''}
                      `}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={togglePasswordVisibility}
                        className="bg-white/10 rounded-full w-8 h-8 flex items-center justify-center hover:bg-brand-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary transition-all"
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? (
                          <Eye className="h-4 w-4 text-brand-primary" />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        )}
                      </Button>
                    </div>

                    {/* Visual highlight effect on focus */}
                    <div className="absolute inset-0 rounded-xl pointer-events-none opacity-0 group-focus-within:opacity-20 bg-gradient-to-r from-brand-primary to-brand-secondary transition-opacity"></div>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-400 mt-1">{errors.password}</p>
                  )}
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="text-right">
                <a href="#" className="text-sm text-gray-300 hover:text-brand-primary transition-colors">
                  Passwort vergessen?
                </a>
              </div>

              {/* Login Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="relative w-full flex justify-center py-3 px-4 border-0 rounded-xl shadow-lg bg-brand-primary text-white text-sm font-medium hover:brightness-110 focus:outline-none focus:ring-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" />
                      <span className="relative">Laden...</span>
                    </div>
                  ) : (
                    <span className="relative">Anmelden</span>
                  )}
                </Button>
              </div>

              {/* Dev Login Bypass Button (Development Only) */}
              {isDevelopment && (
                <div className="pt-4">
                  <Button
                    type="button"
                    onClick={devLoginBypass}
                    disabled={isLoading}
                    className="relative w-full flex justify-center py-2 px-4 border border-amber-400/50 rounded-xl bg-amber-500/10 text-amber-300 text-sm font-medium hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus-visible:ring-2 focus-visible:ring-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>🛠️ Dev Bypass Login (Offline)</span>
                  </Button>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-xs text-gray-400 text-center z-10 relative">
          <p>&copy; {new Date().getFullYear()} Assistent.ai</p>
        </div>
      </div>
    </div>
  )
}
